import clsx from "clsx";
import i18n from "../../util/i18n.ts";
import { PHASE } from "../../../../../common/constants.ts";
import { helpers } from "../../util/helpers.ts";
import { formatSeasonRecord } from "../../util/seasonRecord.ts";
import { useLocalPartial } from "../../util/local.ts";
import React, { memo, type ReactNode, useState } from "react";
import { TeamLogoInline } from "../TeamLogoInline.tsx";
import defaultGameAttributes from "../../../../../common/defaultGameAttributes.ts";
import { PlayerNameLabels } from "../PlayerNameLabels.tsx";
import getWinner from "../../../../../common/getWinner.ts";
import {
  translateTeamName,
  translateTeamRegion,
  translateTeamShortName,
} from "../../util/teamNames.ts";
import { getCol } from "../../util/getCol.ts";
import { getBestPlayerBoxScore } from "../../../../../common/getBestPlayerBoxScore.ts";
import { MobileTooltip } from "../MobileTooltip.tsx";
import { TeamAbbrevText } from "../TeamAbbrevLink.tsx";
import { TeamScoreValue } from "../TeamScoreValue.tsx";
import { formatOvertimeSummary } from "../../util/basketballLabels.ts";
import { TeamRosterSheet } from "../TeamRosterSheet.tsx";
import { MobileStatusMetric } from "../MobileStatusMetric.tsx";
import { getMatchupOutlook, type MatchupOutlookState } from "./statusMetrics.ts";

const roundHalf = (x: number) => {
  return Math.round(x * 2) / 2;
};

type Team = {
  // pts/players are undefined for upcoming games. Others are undefined only for legacy objects
  ovr?: number;
  tid: number;
  won?: number;
  lost?: number;
  tied?: number;
  otl?: number;
  players?: any[];
  playoffs?: {
    seed: number;
    won: number;
    lost: number;
  };

  // Not needed for current season, just use the ones in teamInfoCache. But for past seasons (like on Daily Schedule), this is nice
  branding?: {
    region: string;
    name: string;
    abbrev: string;
    imgURL?: string;
    imgURLSmall?: string;
  };
} & (
  | {
      pts: number;
      sPts?: number;
    }
  | {
      pts?: undefined;
      sPts?: undefined;
    }
);

const getRecord = (t: Team) => {
  if (t.playoffs) {
    return <b>{t.playoffs.won}</b>;
  }

  if (t.won === undefined || t.lost === undefined) {
    return "";
  }

  return formatSeasonRecord({
    lost: t.lost,
    otl: t.otl,
    tied: t.tied,
    won: t.won,
  });
};

const smallStyle = {
  display: "inline-block",
};

const getMatchupOutlookText = (state: MatchupOutlookState) => {
  const key =
    state === "clearAdvantage"
      ? "matchupClearAdvantage"
      : state === "slightAdvantage"
        ? "matchupSlightAdvantage"
        : state === "even"
          ? "matchupEven"
          : state === "slightDisadvantage"
            ? "matchupSlightDisadvantage"
            : "matchupTough";

  return i18n.t(`scoreBox.${key}`, { ns: "ui" });
};

// memo is to prevent completed games from re-rendering in LeagueTopBar
export const ScoreBox = memo(
  ({
    actions = [],
    boxScoreTeamOverride,
    className,
    game,
    playersUpcoming,
    playersUpcomingAbbrev,
    scheduleLayout = false,
    small,
  }: {
    actions?: {
      disabled?: boolean;
      highlight?: boolean;
      href?: string;
      onClick?: () => void;
      text: ReactNode;
    }[];
    boxScoreTeamOverride?: string;
    className?: string;
    game: {
      finals?: boolean;
      forceWin?: number;
      gid: number;
      neutralSite?: boolean;
      season?: number;
      teams: [Team, Team];
      numPeriods?: number;
      overtimes?: number;
    };
    playersUpcoming?: [any, any];
    playersUpcomingAbbrev?: boolean;
    scheduleLayout?: boolean;
    small?: boolean;
  }) => {
    const {
      challengeNoRatings,
      homeCourtAdvantage,
      neutralSite,
      numPeriods,
      phase,
      quarterLength,
      season,
      teamInfoCache,
      userTid,
    } = useLocalPartial([
      "challengeNoRatings",
      "homeCourtAdvantage",
      "neutralSite",
      "numPeriods",
      "phase",
      "quarterLength",
      "season",
      "teamInfoCache",
      "userTid",
    ]);
    const [rosterSheetTarget, setRosterSheetTarget] = useState<
      { season: number; tid: number } | undefined
    >();

    const winner = getWinner(game.teams);

    const final = winner !== undefined;

    const userInGame = userTid === game.teams[0].tid || userTid === game.teams[1].tid;

    let matchupOutlook: ReturnType<typeof getMatchupOutlook> | undefined;
    if (
      !final &&
      !small &&
      userInGame &&
      game.teams[0].ovr !== undefined &&
      game.teams[1].ovr !== undefined
    ) {
      let actualHomeCourtAdvantage;
      if (game.neutralSite) {
        // Completed game at neutral site
        actualHomeCourtAdvantage = 0;
      } else if (neutralSite === "finals" && game.finals) {
        // Upcoming game at neutral site
        actualHomeCourtAdvantage = 0;
      } else if (neutralSite === "playoffs" && phase === PHASE.PLAYOFFS) {
        // Upcoming game at neutral site
        actualHomeCourtAdvantage = 0;
      } else {
        // From @nicidob https://github.com/nicidob/bbgm/blob/master/team_win_testing.ipynb
        // Default homeCourtAdvantage is 1
        actualHomeCourtAdvantage = 3.3504 * homeCourtAdvantage;
      }

      const ovr0 = game.teams[0].ovr;
      const ovr1 = game.teams[1].ovr;
      let projectedMarginForTeamZero = (15 / 50) * (ovr0 - ovr1) + actualHomeCourtAdvantage;

      // Adjust for game length
      projectedMarginForTeamZero *=
        (numPeriods * quarterLength) /
        (defaultGameAttributes.numPeriods * defaultGameAttributes.quarterLength);

      matchupOutlook = getMatchupOutlook({
        projectedMarginForTeamZero: roundHalf(projectedMarginForTeamZero),
        userTeamIndex: userTid === game.teams[0].tid ? 0 : 1,
      });
    }

    const overtimes = formatOvertimeSummary(game.overtimes);

    const gameSeason = game.season ?? season;

    const allStarGame = game.teams[0].tid === -1 && game.teams[1].tid === -2;
    const tradeDeadline = game.teams[0].tid === -3 && game.teams[1].tid === -3;
    const gameStatus = tradeDeadline ? "trade-deadline" : final ? "final" : "scheduled";
    const gameStatusLabel = tradeDeadline
      ? i18n.t("scoreBox.tradeDeadline", {
          ns: "ui",
          defaultValue: "Trade Deadline",
        })
      : final
        ? i18n.t("scoreBox.finalStatus", {
            ns: "ui",
            defaultValue: "Final",
          })
        : i18n.t("scoreBox.scheduledStatus", {
            ns: "ui",
            defaultValue: "Scheduled",
          });
    const topPerformerLabel = final
      ? i18n.t("scoreBox.topPerformer", {
          ns: "ui",
          defaultValue: "Top performer",
        })
      : i18n.t("scoreBox.keyPlayer", {
          ns: "ui",
          defaultValue: "Key player",
        });

    if (actions.length === 0 && final && !small) {
      actions = [
        {
          text: i18n.t("scoreBox.boxScore", { ns: "ui", defaultValue: "Box score" }),
          href: helpers.leagueUrl([
            "game_log",
            allStarGame
              ? "special"
              : boxScoreTeamOverride !== undefined
                ? boxScoreTeamOverride
                : `${game.teams[0].branding?.abbrev ?? teamInfoCache[game.teams[0].tid]?.abbrev}_${
                    game.teams[0].tid
                  }`,
            gameSeason,
            game.gid,
          ]),
        },
      ];
    }

    const scoreBox = (
      <div
        className={clsx(
          "score-box-manager-component flex-grow-1 w-100",
          small ? "d-flex score-box-manager-component-small" : undefined,
          scheduleLayout && !small ? "score-box-schedule-layout" : undefined,
        )}
        data-score-box-gid={game.gid}
        data-game-status={gameStatus}
        data-score-box-size={small ? "small" : "normal"}
        style={small ? smallStyle : undefined}
      >
        {/* C1 (B2a leftover, 法则二): the status word appears at most once per
				card — the "Final score: X-Y" text row duplicated both the chip's
				status word and the per-team scores below, so it is gone. The value
				stays machine-readable via above. */}
        {!small ? (
          <div className="score-box-status-row d-flex flex-wrap gap-2 p-1 small text-body-secondary">
            <span
              className="score-box-status badge text-bg-light border"
              data-game-status={gameStatus}
            >
              {gameStatusLabel}
            </span>
          </div>
        ) : null}
        {matchupOutlook ? (
          <div
            className="score-box-spread px-1 pb-2"
            data-game-status={gameStatus}
            data-score-or-spread="spread"
            data-scorebox-matchup-outlook={matchupOutlook.state}
          >
            <MobileStatusMetric
              detail={i18n.t("scoreBox.matchupOutlookDetail", {
                ns: "ui",
                defaultValue: "Based on team strength and game location.",
              })}
              label={i18n.t("scoreBox.matchupOutlook", {
                ns: "ui",
                defaultValue: "Matchup outlook",
              })}
              score={matchupOutlook.score}
              value={getMatchupOutlookText(matchupOutlook.state)}
            />
          </div>
        ) : null}
        <div
          className={clsx(
            "score-box-surface border-light",
            actions.length > 0 ? "border-end-0" : undefined,
          )}
        >
          {tradeDeadline ? (
            <div
              className={`${
                small ? "score-box-deadline-small" : "score-box-deadline"
              } p-1 d-flex align-items-center ms-1`}
            >
              {small ? (
                i18n.t("scoreBox.tradeDeadline", { ns: "ui", defaultValue: "Trade Deadline" })
              ) : (
                <h2 className="mb-0">
                  {i18n.t("scoreBox.tradeDeadline", { ns: "ui", defaultValue: "Trade Deadline" })}
                </h2>
              )}
            </div>
          ) : allStarGame && !final ? (
            [1, 2].map((i) => (
              <div
                className={clsx("d-flex align-items-center", {
                  "score-box-all-star": !small,
                })}
                key={i}
              >
                <div className={clsx("p-1", { "pe-5": small })}>
                  <a
                    href={helpers.leagueUrl(["daily_schedule"])}
                    className={!small ? "fw-bold" : undefined}
                  >
                    {i18n.t("allStarTeams.genericTeamName", {
                      ns: "ui",
                      num: i,
                      defaultValue: "All-Stars {{num}}",
                    })}
                  </a>
                </div>
              </div>
            ))
          ) : (
            [1, 0].map((i) => {
              const t = game.teams[i]!;
              const teamOutcome = !final
                ? "pending"
                : winner === -1
                  ? "tie"
                  : winner === i
                    ? "win"
                    : "loss";
              let scoreClass;
              let scoreClassForceWin;
              if (winner !== undefined) {
                if (winner === i) {
                  if (userInGame) {
                    if (t.tid === userTid) {
                      scoreClass = "alert-success";
                    } else {
                      scoreClass = "alert-danger";
                    }
                  }

                  if (game.forceWin !== undefined) {
                    scoreClassForceWin = "alert-forced-result";
                  }
                } else if (winner === -1 && userInGame && t.tid === userTid) {
                  // Tie
                  scoreClass = "alert-warning";

                  if (game.forceWin !== undefined) {
                    scoreClassForceWin = "alert-forced-result";
                  }
                }
              }
              if (!scoreClassForceWin) {
                scoreClassForceWin = scoreClass;
              }

              let imgURL;
              let teamName;
              if (allStarGame) {
                imgURL = "/ico/icon.svg";
                teamName = i18n.t("allStarTeams.genericTeamName", {
                  ns: "ui",
                  num: i === 0 ? 2 : 1,
                  defaultValue: "All-Stars {{num}}",
                });
              } else {
                const branding = t.branding ?? teamInfoCache[t.tid];
                if (branding) {
                  imgURL = branding.imgURLSmall ?? branding.imgURL;
                  teamName = small
                    ? translateTeamShortName(branding, branding.abbrev)
                    : `${translateTeamRegion(branding.region)} ${translateTeamName(branding.name)}`;
                }
              }

              // For @MikeHoudini on Discord
              const userTeamClass = t.tid === userTid && final ? "user-team" : undefined;

              let p;
              let playerStatText;
              if (playersUpcoming?.[i]) {
                p = playersUpcoming?.[i];
                playerStatText = (
                  <>
                    {playersUpcomingAbbrev ? (
                      <>
                        <button
                          className="btn btn-link p-0 align-baseline"
                          onClick={() => setRosterSheetTarget({ season: gameSeason, tid: t.tid })}
                          type="button"
                        >
                          <TeamAbbrevText tid={p.tid} abbrev={p.abbrev} />
                        </button>
                        ,{" "}
                      </>
                    ) : null}
                    {!challengeNoRatings
                      ? i18n.t("scoreBox.playerOverallRating", {
                          ns: "ui",
                          ovr: p.ratings.ovr,
                          defaultValue: "{{ovr}} overall",
                        })
                      : null}
                    {`${
                      !challengeNoRatings ? ", " : ""
                    }${p.stats.pts.toFixed(1)} / ${p.stats.trb.toFixed(
                      1,
                    )} / ${p.stats.ast.toFixed(1)}`}
                  </>
                );
              } else if (final && t.players) {
                const best = getBestPlayerBoxScore(t.players);
                if (best) {
                  p = best.p;
                  playerStatText = best.statTexts.map((stat, i) => {
                    const col = getCol(`stat:${stat}`);

                    const title = col.title;

                    return (
                      <React.Fragment key={stat}>
                        {i > 0 ? ", " : null}
                        <MobileTooltip title={col.desc}>
                          <span>
                            {best.processedStats[stat]} {title}
                          </span>
                        </MobileTooltip>
                      </React.Fragment>
                    );
                  });
                }
              }

              const WINNER_ARROW_WIDTH = 6;

              return (
                <div
                  key={i}
                  className={clsx(
                    "score-box-team d-flex align-items-center alert-bg-color",
                    scoreClassForceWin,
                    userTeamClass,
                  )}
                  data-team-outcome={teamOutcome}
                  data-team-result-forced={
                    game.forceWin !== undefined && final ? "true" : undefined
                  }
                >
                  {imgURL || scheduleLayout ? (
                    <button
                      aria-label={teamName}
                      className="btn btn-link p-0 border-0"
                      disabled={allStarGame}
                      onClick={() => setRosterSheetTarget({ season: gameSeason, tid: t.tid })}
                      type="button"
                    >
                      <TeamLogoInline
                        imgURL={imgURL}
                        includePlaceholderIfNoLogo
                        size={small ? 24 : scheduleLayout ? 40 : 36}
                        className={scheduleLayout ? undefined : "ms-1"}
                      />
                    </button>
                  ) : null}
                  <div
                    className={clsx("d-flex align-items-center score-box-left-wrapper", {
                      "flex-grow-1": small || scheduleLayout,
                    })}
                    style={
                      !small && !scheduleLayout
                        ? {
                            width: 210,
                          }
                        : undefined
                    }
                  >
                    <div className="flex-grow-1 text-truncate p-1">
                      {t.playoffs ? <span className="text-dark">{t.playoffs.seed}. </span> : null}
                      <button
                        className={clsx(
                          "btn btn-link p-0 text-start",
                          !small ? "fw-bold" : undefined,
                        )}
                        disabled={allStarGame}
                        onClick={() => setRosterSheetTarget({ season: gameSeason, tid: t.tid })}
                        type="button"
                      >
                        {teamName}
                      </button>
                      {!small && !scheduleLayout ? (
                        <div className="text-body-secondary text-truncate">{getRecord(t)}</div>
                      ) : null}
                    </div>
                    {final ? (
                      <div
                        className={clsx(
                          "score-box-score-value text-body text-end align-self-stretch d-flex align-items-center alert-bg-color",
                          scoreClass,
                          userTeamClass,
                          {
                            "fw-bold score-box-score": small,
                            "ps-2": !small,
                            "pe-1": small,
                          },
                        )}
                        data-game-status={gameStatus}
                        data-score-or-spread="score"
                        style={!small && !scheduleLayout ? { fontSize: 16 } : undefined}
                      >
                        {small ? (
                          <a
                            href={helpers.leagueUrl([
                              "game_log",
                              allStarGame ? "special" : `${teamInfoCache[t.tid]?.abbrev}_${t.tid}`,
                              gameSeason,
                              game.gid,
                            ])}
                            className="d-block w-100 h-100"
                            style={{
                              // Vertical center
                              paddingTop: 4,
                            }}
                          >
                            <TeamScoreValue pts={t.pts} sPts={t.sPts} />
                          </a>
                        ) : (
                          <>
                            <span
                              className={
                                winner !== i && winner !== -1 ? "text-body-secondary" : undefined
                              }
                            >
                              <TeamScoreValue
                                pts={t.pts}
                                sPts={t.sPts}
                                shootoutClassName="text-body-secondary"
                              />
                            </span>
                            <div className="ms-1">
                              {winner === i ? (
                                <svg
                                  viewBox="0 0 50 100"
                                  xmlns="http://www.w3.org/2000/svg"
                                  width={WINNER_ARROW_WIDTH}
                                  height={WINNER_ARROW_WIDTH * 2}
                                  className="align-baseline"
                                  style={{ fill: "var(--bs-gray-600)" }}
                                >
                                  <polygon points="0,50 50,0 50,100 0,50" />
                                </svg>
                              ) : (
                                <div style={{ width: WINNER_ARROW_WIDTH }} />
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ) : null}
                  </div>
                  {p && !scheduleLayout ? (
                    <div
                      className="align-self-stretch border-start ps-2 flex-grow-1 text-body-secondary d-none d-sm-flex align-items-center overflow-hidden text-nowrap"
                      data-scorebox-top-player-kind={final ? "performer" : "key-player"}
                      data-scorebox-top-player-pid={p.pid}
                      style={{
                        backgroundColor: "var(--bs-white)",
                        width: 200,
                      }}
                    >
                      <div>
                        <div className="small text-body-secondary">{topPerformerLabel}</div>
                        <div>
                          <PlayerNameLabels
                            pid={p.pid}
                            injury={p.injury}
                            pos={p.ratings?.pos ?? p.pos}
                            season={season}
                            legacyName={p.name}
                          />
                        </div>
                        <div>{playerStatText}</div>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
        {small && overtimes ? (
          <div
            className="text-end text-body-secondary px-1 d-flex align-items-center"
            style={{ height: 28 }}
          >
            {overtimes}
          </div>
        ) : null}
      </div>
    );

    const teamRosterSheet = (
      <TeamRosterSheet
        onClose={() => setRosterSheetTarget(undefined)}
        open={rosterSheetTarget !== undefined}
        season={rosterSheetTarget?.season}
        tid={rosterSheetTarget?.tid}
      />
    );

    if (actions.length > 0) {
      return (
        <>
          <div className={className}>
            <div className="d-flex flex-column flex-sm-row">
              {scoreBox}
              <div className="btn-group score-box-action">
                {actions.map((action, i) => {
                  const actionRole = action.highlight ? "primary" : "secondary";
                  const classNameAction = clsx(
                    "btn",
                    "score-box-action-button",
                    action.highlight ? "btn-primary" : "btn-light-bordered-2",
                  );

                  return action.onClick ? (
                    <button
                      key={i}
                      className={classNameAction}
                      data-action-role={actionRole}
                      disabled={action.disabled}
                      onClick={action.onClick}
                    >
                      {action.text}
                    </button>
                  ) : (
                    <a
                      key={i}
                      className={`${classNameAction} d-flex align-items-center`}
                      data-action-role={actionRole}
                      href={action.href}
                    >
                      {action.text}
                    </a>
                  );
                })}
              </div>
            </div>
            {!small && overtimes ? (
              <div className="text-body-secondary p-1">{overtimes}</div>
            ) : null}
          </div>
          {teamRosterSheet}
        </>
      );
    }

    return (
      <>
        <div className={className}>{scoreBox}</div>
        {teamRosterSheet}
      </>
    );
  },
);
